/**
 * Recruiter / company catalogue. Slug used at /recruiters/:slug.
 */

export interface RecruiterRole {
  title: string;
  branches: string[];
  ctc: string;
  format: "full-time" | "internship" | "ppo";
}

export interface RecruiterAlumnus {
  name: string;
  role: string;
  batch: string;
  city: string;
}

export interface Recruiter {
  slug: string;
  name: string;
  sector: string;
  /** Top tier IT services / Product / Core etc. */
  tier: string;
  /** "53 OFFERS" — short tag shown on placements wall. */
  offers: string;
  /** Long-form intro paragraphs. */
  intro: string[];
  /** History of campus visits — most recent first. */
  history: Array<{ cycle: string; offers: number; note?: string }>;
  roles: RecruiterRole[];
  alumni: RecruiterAlumnus[];
  process: string[];
  contact: { tnpEmail: string; recruiterPortal?: string };
}

export const RECRUITERS: Recruiter[] = [
  {
    slug: "tcs",
    name: "Tata Consultancy Services",
    sector: "IT Services",
    tier: "Tier-1 IT services · Tata Group",
    offers: "53 OFFERS",
    intro: [
      "Tata Consultancy Services has been one of BIET's most consistent recruiters since 2003. The company runs three drives per academic year — Ninja, Digital and Prime — across CSE, ISE, ECE and AIML branches. Cumulative offers from TCS at BIET have crossed 200 across the past five years.",
      "TCS hires through a multi-stage process that begins with the National Qualifier Test (NQT) in the fifth semester. Candidates clearing the NQT are invited to on-campus drives held in November and February.",
    ],
    history: [
      { cycle: "Feb 2026", offers: 53, note: "2 Prime, 6 Digital, 45 Ninja" },
      { cycle: "Aug 2025", offers: 38, note: "Pre-final-year cohort" },
      { cycle: "Feb 2025", offers: 41 },
      { cycle: "Aug 2024", offers: 36 },
      { cycle: "Feb 2024", offers: 32 },
    ],
    roles: [
      { title: "Ninja — Systems Engineer", branches: ["CSE", "ISE", "ECE", "AIML"], ctc: "₹3.36 LPA", format: "full-time" },
      { title: "Digital — Software Engineer", branches: ["CSE", "ISE", "AIML"], ctc: "₹7.09 LPA", format: "full-time" },
      { title: "Prime — Specialist Engineer", branches: ["CSE", "AIML"], ctc: "₹9.09 LPA", format: "full-time" },
    ],
    alumni: [
      { name: "Alumnus 11", role: "Senior Systems Engineer", batch: "CSE · 2022", city: "Bengaluru" },
      { name: "Alumnus 12", role: "Specialist Engineer", batch: "AIML · 2023", city: "Pune" },
      { name: "Alumnus 13", role: "Project Lead", batch: "ISE · 2018", city: "Chennai" },
    ],
    process: [
      "TCS NQT — National Qualifier Test (online, ~3 hours)",
      "Technical interview (45 mins, on-campus)",
      "Managerial round (30 mins)",
      "HR round (15 mins)",
    ],
    contact: { tnpEmail: "tnp.tcs@bietdvg.edu", recruiterPortal: "https://nextstep.tcs.com" },
  },
  {
    slug: "infosys",
    name: "Infosys",
    sector: "IT Services",
    tier: "Tier-1 IT services",
    offers: "27 OFFERS",
    intro: [
      "Infosys recruits annually from BIET across the Power Programmer, Specialist Programmer and Systems Engineer tracks. The company conducts an InfyTQ certification programme that pre-final-year students at BIET have access to throughout the third year — clearing it improves callback chances substantially.",
    ],
    history: [
      { cycle: "Feb 2026", offers: 27 },
      { cycle: "Aug 2025", offers: 22 },
      { cycle: "Feb 2025", offers: 24 },
    ],
    roles: [
      { title: "Systems Engineer", branches: ["CSE", "ISE", "ECE"], ctc: "₹3.6 LPA", format: "full-time" },
      { title: "Specialist Programmer", branches: ["CSE", "AIML"], ctc: "₹6.5 LPA", format: "full-time" },
      { title: "Power Programmer", branches: ["CSE", "AIML"], ctc: "₹9 LPA", format: "full-time" },
    ],
    alumni: [
      { name: "Alumnus 14", role: "Senior Systems Engineer", batch: "ISE · 2021", city: "Mysuru" },
      { name: "Alumnus 15", role: "Power Programmer", batch: "CSE · 2024", city: "Bengaluru" },
    ],
    process: [
      "InfyTQ certification (preferred)",
      "Aptitude + coding test (online)",
      "Technical + HR combined interview",
    ],
    contact: { tnpEmail: "tnp.infosys@bietdvg.edu" },
  },
  {
    slug: "robert-bosch",
    name: "Robert Bosch",
    sector: "Engineering · Automotive",
    tier: "Core engineering · German MNC",
    offers: "6",
    intro: [
      "Robert Bosch's Bengaluru division has hired BIET mechanical and electronics engineers consistently since 2010. The drive is small — typically 5-7 offers per cycle — but the bar is high. Pre-placement offers are extended after the structured eight-week summer internship.",
      "Roles cover design, verification, embedded systems and powertrain engineering. Many BIET alumni at Bosch transition to the German parent within five years.",
    ],
    history: [
      { cycle: "Jan 2026", offers: 6, note: "Pre-placement drive · MECH only" },
      { cycle: "Aug 2025", offers: 4, note: "Internship-to-PPO" },
      { cycle: "Aug 2024", offers: 5 },
    ],
    roles: [
      { title: "Design Engineer", branches: ["MECH"], ctc: "₹8 LPA", format: "full-time" },
      { title: "Verification Engineer", branches: ["ECE"], ctc: "₹8 LPA", format: "full-time" },
      { title: "Summer Intern (8 weeks)", branches: ["MECH", "ECE"], ctc: "₹40,000/mo", format: "internship" },
    ],
    alumni: [
      { name: "Alumnus 16", role: "Senior Design Engineer", batch: "MECH · 2018", city: "Stuttgart" },
      { name: "Alumnus 17", role: "Engineering Lead", batch: "MECH · 2008", city: "Bengaluru" },
    ],
    process: [
      "Coding + technical aptitude (online)",
      "Domain technical interview (60 mins, in-person)",
      "Design challenge / case study",
      "HR + leadership interview",
    ],
    contact: { tnpEmail: "tnp.bosch@bietdvg.edu" },
  },
  {
    slug: "goldman-sachs",
    name: "Goldman Sachs",
    sector: "Financial Services · Technology",
    tier: "Investment Bank · Bulge bracket",
    offers: "4",
    intro: [
      "Goldman Sachs visited BIET for the first time in December 2025 for its Bengaluru technology team. The drive was open exclusively to ISE and CSE pre-final-year students. Four ISE students received full-time offers for VP-track engineering roles after a four-stage interview spanning four hours.",
    ],
    history: [
      { cycle: "Dec 2025", offers: 4, note: "First-ever campus drive · ISE only" },
    ],
    roles: [
      { title: "Software Engineer (VP-track)", branches: ["CSE", "ISE"], ctc: "₹26 LPA", format: "full-time" },
    ],
    alumni: [
      { name: "Alumnus 18", role: "VP, Technology", batch: "ISE · 2013", city: "Bengaluru" },
    ],
    process: [
      "Online coding test (HackerRank · 90 mins)",
      "Two technical interview rounds (system design + algorithms)",
      "Behavioural / Goldman fit interview",
      "Final hiring committee review",
    ],
    contact: { tnpEmail: "tnp.gs@bietdvg.edu" },
  },
  {
    slug: "cognizant",
    name: "Cognizant",
    sector: "IT Services",
    tier: "Tier-1 IT services",
    offers: "21 OFFERS",
    intro: [
      "Cognizant's Generation Cognizant (GenC) hiring programme has visited BIET annually since 2015. The drive splits offers across GenC, GenC Pro, GenC Elevate and GenC Next tracks — each with a different CTC band and skill profile.",
    ],
    history: [
      { cycle: "Feb 2026", offers: 21 },
      { cycle: "Feb 2025", offers: 18 },
    ],
    roles: [
      { title: "GenC", branches: ["CSE", "ISE", "ECE"], ctc: "₹4 LPA", format: "full-time" },
      { title: "GenC Elevate", branches: ["CSE", "ISE"], ctc: "₹6.75 LPA", format: "full-time" },
      { title: "GenC Next", branches: ["CSE", "AIML"], ctc: "₹9 LPA", format: "full-time" },
    ],
    alumni: [
      { name: "Alumnus 19", role: "Senior Engineer", batch: "CSE · 2020", city: "Chennai" },
    ],
    process: [
      "Aptitude + technical MCQ (online)",
      "Coding round (online, two problems)",
      "Technical + HR interview (combined)",
    ],
    contact: { tnpEmail: "tnp.cognizant@bietdvg.edu" },
  },
  {
    slug: "amazon",
    name: "Amazon",
    sector: "Product Engineering · E-commerce",
    tier: "Big-tech · Product",
    offers: "2",
    intro: [
      "Amazon visits BIET selectively for its Bengaluru and Hyderabad technology teams. Selection is highly competitive — typically two to three offers per cycle — through a five-round interview that emphasises algorithms, system design and Amazon's leadership principles.",
    ],
    history: [
      { cycle: "Dec 2025", offers: 2 },
      { cycle: "Dec 2024", offers: 3 },
    ],
    roles: [
      { title: "Software Development Engineer (SDE-1)", branches: ["CSE", "AIML"], ctc: "₹44 LPA", format: "full-time" },
    ],
    alumni: [
      { name: "Alumnus 03", role: "Engineering Manager", batch: "CSE · 2012", city: "Bengaluru" },
    ],
    process: [
      "Online assessment (debugging + algorithms · 2 hours)",
      "Technical interview · 1 (algorithms + DS)",
      "Technical interview · 2 (system design)",
      "Bar raiser interview (45 mins)",
      "HR + leadership-principles interview",
    ],
    contact: { tnpEmail: "tnp.amazon@bietdvg.edu" },
  },
];

export function getRecruiter(slug: string): Recruiter | undefined {
  return RECRUITERS.find((r) => r.slug === slug);
}

/**
 * The recruitment-partner wall on /placements, in the order the Placement Cell
 * supplied it (2026-09-04).
 *
 * This is a curated list, NOT derived from the drive sheet — the wall used to
 * rank the top 24 employers by offers made, which surfaced whichever companies
 * happened to hire in volume rather than the partners the institute wants to
 * lead with. `placements.ts` still holds the sheet, and the figures elsewhere
 * on the page still come from it.
 *
 * Two entries carry a note the Cell added; the wall shows the company name and
 * the note is kept here rather than printed on the tile.
 */
export const RECRUITMENT_PARTNERS: string[] = [
  "Tata Consultancy Services", // supplied as "(Priority Institute)"
  "Cognizant",
  "MuSigma",
  "IBM",
  "Deloitte",
  "KPMG",
  "Accenture",
  "EY",
  "Nomiso",
  // Supplied twice, as "Sasken" (10) and "Sasken Technologies" (28). Kept once,
  // at the first position, under the fuller name.
  "Sasken Technologies",
  "Protech", // supplied as "(Japan Based Company)"
  "Cleo Software",
  "Maple Labs",
  "Hashedin Technology",
  "LTI Mindtree",
  // Not on the Cell's supplied list — added to bring the wall to 36 so its
  // six-column grid fills exactly. Infosys is a real 2026 recruiter in the
  // drive sheet and already has a /recruiters/infosys page, so it links.
  // Placed with the other IT-services names rather than appended.
  "Infosys",
  "Pragathi / Prerana Engg Works",
  "PRDC",
  "Grasim Industries",
  "Aarbee Structures",
  "ASC International",
  "Blanco Steel Detailing",
  "Herin Electronics", // supplied all-caps; title-cased to match the wall
  "JSW",
  "Schneider Electric",
  "Bosch Global Services",
  "DMG MORI India", // DMG MORI styles its own name in caps
  "Tessolve Semiconductors",
  "Zosh Aerospace",
  "Anora Semiconductors",
  "Mercedes Benz R&D",
  "Arvind Mill",
  "Jockey India",
  "Shahi Exports",
  "Gokaldas Exports",
  "CuraTeQ Biologics",
];

/** Maps the short company name shown in Placements.tsx → slug for hyperlinking. */
export const RECRUITER_NAME_TO_SLUG: Record<string, string> = {
  "TCS": "tcs",
  "Tata Consultancy Services": "tcs",
  "Infosys": "infosys",
  "Robert Bosch": "robert-bosch",
  "Goldman Sachs": "goldman-sachs",
  "Cognizant": "cognizant",
  "Amazon": "amazon",
};
